import React, { useState } from 'react';
import apiClient from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';

const Section = ({ num, title, children }) => (
    <div className="relative pl-4 border-l-2 border-cyan-500/40">
        <h3 className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em] mb-1.5">
            [{String(num).padStart(2, '0')}]
        </h3>
        <h4 className="font-semibold text-slate-100 mb-2">{title}</h4>
        {children}
    </div>
);

const DataSecurityGuidelinesModal = () => {
    const { markPoliciesAccepted } = useAuth();
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleAccept = async () => {
        if (!isChecked || isSubmitting) return;
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await apiClient.post('/users/me/accept-policies');
            if (response.data?.success) {
                markPoliciesAccepted();
            } else {
                setError(response.data?.message || 'Failed to record acceptance. Please try again.');
            }
        } catch (e) {
            console.error('accept-policies failed:', e);
            setError('Network error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const btnDisabled = !isChecked || isSubmitting;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-xl backdrop-saturate-150 px-4"
            aria-modal="true"
            role="dialog"
        >
            <div
                className="max-w-4xl w-full relative overflow-hidden rounded-2xl border border-cyan-500/40 bg-gradient-to-b from-slate-900 to-slate-950 p-6 md:p-8 text-slate-100 shadow-2xl"
                style={{
                    boxShadow: '0 0 0 1px rgba(6,182,212,0.1), 0 0 40px rgba(6,182,212,0.12), 0 25px 50px -12px rgba(0,0,0,0.5)',
                }}
            >
                {/* subtle grid */}
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: 'linear-gradient(to right, rgb(6 182 212) 1px, transparent 1px), linear-gradient(to bottom, rgb(6 182 212) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                />

                <div className="relative">
                    {/* <p className="font-mono text-xs text-cyan-400/80 uppercase tracking-[0.25em] mb-2">
            // Policy · One-time acceptance
          </p> */}
                    <h2
                        className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400 bg-clip-text text-transparent"
                        style={{ textShadow: '0 0 40px rgba(6,182,212,0.3)' }}
                    >
                        Data Security &amp; Privacy Guidelines
                    </h2>
                    <p className="text-sm md:text-base text-slate-400 mb-5 leading-relaxed">
                        To protect your organization&apos;s data while using Cyber Jugs, you must review and accept
                        the following guidelines. This is required only once per account and applies to all
                        future sessions.
                    </p>

                    <div
                        className="relative rounded-xl border border-slate-700/80 bg-slate-900/70 p-4 md:p-6 max-h-96 md:max-h-[32rem] overflow-y-auto text-sm md:text-base space-y-5"
                        style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}
                    >
                        <Section num={1} title="Handling of Sensitive Information">
                            <ul className="space-y-1.5 text-slate-300">
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Do not upload or enter real customer, employee, or production data in simulations, challenges, or any platform field.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Use only anonymized, synthetic, or approved demo data in all exercises, sandboxes, and AI-assisted scenarios.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Never share your login credentials, MFA codes, recovery keys, or API tokens with anyone—including colleagues or support, unless via official, verified channels.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Do not paste, type, or upload proprietary code, internal documents, or confidential communications into the platform.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    If you accidentally expose sensitive information, report it immediately to your administrator or security team.
                                </li>
                            </ul>
                        </Section>

                        <Section num={2} title="Responsible Use of the Platform">
                            <ul className="space-y-1.5 text-slate-300">
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Use all training content—simulations, challenges, modules, and AI tools—solely for learning, awareness, and skill development.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Do not attempt to attack, probe, or exploit real systems, networks, or services based on techniques or scenarios shown here.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Report any suspected vulnerabilities, data leaks, or security issues in the platform to your administrator or the designated contact.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Do not use the platform to circumvent organizational security controls, exfiltrate data, or conduct unauthorized testing.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Follow your organization&apos;s acceptable-use and IT security policies when accessing Cyber Jugs from work or corporate devices.
                                </li>
                            </ul>
                        </Section>

                        <Section num={3} title="Privacy &amp; Monitoring">
                            <ul className="space-y-1.5 text-slate-300">
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Your activity—including scores, attempts, progress, and time spent—may be logged for learning analytics, compliance, and platform improvement.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Personal and usage data are processed according to your organization&apos;s security and privacy policies, and applicable regulations.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Do not store personal passwords, PINs, or confidential notes inside the platform; use a designated password manager or secure storage.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    Admin and support personnel may access usage data when necessary for security, support, or auditing purposes.
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-cyan-400/80 font-mono">▹</span>
                                    You are responsible for protecting your session: lock your device when away and log out when using shared or public computers.
                                </li>
                            </ul>
                        </Section>

                        <Section num={4} title="Compliance Acknowledgement">
                            <p className="text-slate-300 leading-relaxed mb-2">
                                By clicking &quot;I Agree&quot;, you confirm that you have read, understood, and will comply with these Data Security &amp; Privacy Guidelines, and with your organization&apos;s information security and privacy policies, while using Cyber Jugs. Non-compliance may result in restricted access, disciplinary action, or other consequences as defined by your organization.
                            </p>
                            <p className="text-slate-400 text-sm leading-relaxed">
                                If you have questions about these guidelines, contact your administrator or your organization&apos;s security team before proceeding.
                            </p>
                        </Section>
                    </div>

                    <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <label className="inline-flex items-start gap-3 cursor-pointer select-none group">
                            <span className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded border border-slate-500 bg-slate-800/80 transition-all group-hover:border-cyan-500/50 group-focus-within:border-cyan-400 group-focus-within:ring-2 group-focus-within:ring-cyan-400/30">
                                <input
                                    type="checkbox"
                                    className="absolute inset-0 cursor-pointer opacity-0"
                                    checked={isChecked}
                                    onChange={(e) => setIsChecked(e.target.checked)}
                                    disabled={isSubmitting}
                                />
                                {isChecked && (
                                    <span className="font-mono text-cyan-400 text-sm" style={{ textShadow: '0 0 8px rgba(6,182,212,0.6)' }}>
                                        ✓
                                    </span>
                                )}
                            </span>
                            <span className="text-sm md:text-base text-slate-300 group-hover:text-slate-200">
                                I have read and agree to the Data Security &amp; Privacy Guidelines.
                            </span>
                        </label>

                        <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-center">
                            {error && (
                                <p className="text-xs md:text-sm text-red-400 max-w-xs font-mono">
                                    {error}
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={handleAccept}
                                disabled={btnDisabled}
                                className={`
                  relative px-6 py-2.5 rounded-lg text-sm md:text-base font-semibold uppercase tracking-wider transition-all duration-200
                  ${btnDisabled
                                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white border border-cyan-400/30 hover:shadow-[0_0_24px_rgba(6,182,212,0.4)] hover:border-cyan-400/50'
                                    }
                `}
                            >
                                {isSubmitting ? (
                                    <span className="inline-flex items-center gap-2">
                                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-cyan-400" />
                                        Saving...
                                    </span>
                                ) : (
                                    'I Agree'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataSecurityGuidelinesModal;
